/**
 *
 * CoursesDetail
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import { flushState, loadCourse } from "./actions";
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemData,
} from "./selectors";

// antd component
import { Col, Row, Tabs, message } from "antd";
const { TabPane } = Tabs;

// components
import Preloader from "components/Preloader";
import CustomPageHeader from "components/CustomPageHeader";
import CustomForm from "components/Forms/CustomForm";
import InfoCard from "components/InfoCard";
import DeleteButton from "components/DeleteButton";

// Custom Tabs
import TabStudents from "components/Tabs/TabStudents";
import TabTeachers from "components/Tabs/TabTeachers";
import RenderPopconfirm from "components/RenderPopconfirm";

// utils
import { convertTimeToString } from 'utils/moment/moment.utils';
import {
  isEqualFormValues,
  checkTimeSetDefaultRange,
} from "utils/forms/forms.utils";
import _local from "utils/localStorageUtils";
import { getDayOfWeekObj } from "utils/helpers/helper.generateData";

export function CoursesDetail({
  flushState,
  mode,
  // reducer main props
  loading,
  itemData,
  loadCourse,
  hardDeleteLoading,
  // custom props
  currentId,
  update,
  updatedItem,
  hardDeleteItem,
  // form actions and data
  form,
  generalForm,
  // parent states
  formHaveChanges,
  setFormHaveChanges,
  currentElement,
  setCurrentElement,
}) {
  useInjectReducer({ key: "coursesDetail", reducer });
  useInjectSaga({ key: "coursesDetail", saga });

  const { t, i18n } = useTranslation();

  const location = useLocation();

  const localSchool = _local.getLocalSchool();

  const newKey = "new";

  const [initID, setInitID] = useState(currentId);

  // states for tabs toggle
  const [activeTab, setActiveTab] = useState("general");
  const [stayActiveTab, setStayActiveTab] = useState(false);
  // states for main data
  const [currentStudents, setCurrentStudents] = useState([]);
  const [currentTeachers, setCurrentTeachers] = useState([]);

  const [formMethod, setFormMethod] = useState("update");
  const [currentMode, setCurrentMode] = useState(mode);

  // Form initialization for each tab
  const {
    customForm,
    resetForm,
    submitForm,
    setFormData,
    setFormFieldsValues,
  } = form;

  // Clearing the state after unmounting a component
  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // reset data in the general form
    resetForm();
    setFormFieldsValues({
      level: 0,
    });
    if (!currentId || currentId == newKey) {
      // Set default data for NEW Item
      setCurrentElement(false);
      setActiveTab("general");
      if (currentId == newKey) {
        setCurrentMode("new");
        setFormMethod(newKey);
        const locationStateLevel = location?.state?.level || false;
        const locationStateClass = location?.state?.classes || false;
        const locationStateYeaer = location?.state?.year || false;
        if (
          !!locationStateLevel ||
          !!locationStateClass ||
          !!locationStateYeaer
        ) {
          let initValue = {
            course_week: t("global.dayOfWeek.monday.value"),
          };
          locationStateLevel && (initValue["level"] = locationStateLevel);
          if (locationStateClass?.length > 0) {
            initValue["classes"] = locationStateClass || [];
          }
          locationStateYeaer && (initValue["year"] = locationStateYeaer);

          setCurrentElement(initValue);
          setFormData(initValue);
        }
      }
    } else if (currentId && currentId !== newKey) {
      setCurrentMode("edit");
      setFormMethod("update");
      setCurrentElement(false);
      loadCourse(currentId);
    }

    setInitID(currentId);
  }, [currentId]);

  // Set data for opened item
  useEffect(() => {
    if (itemData || updatedItem) {
      // check if updated data the same as current data
      let initData;
      if (currentId == updatedItem?._id || currentId == newKey) {
        initData = updatedItem;
        if (currentId == newKey) {
          setCurrentMode("new");
          setFormMethod(newKey);
        } else if (currentId == updatedItem?._id) {
          setCurrentMode("edit");
          setFormMethod("update");
        }
      } else {
        initData =
          itemData?._id == updatedItem?._id
            ? updatedItem || itemData
            : itemData;
        setCurrentMode("edit");
        setFormMethod("update");
      }

      setInitID(initData?._id);
      setCurrentElement(initData);

      // set students list
      if (
        JSON.stringify(currentStudents) !== JSON.stringify(initData?.students)
      ) {
        setCurrentStudents(initData?.students);
      }

      // set teachers list
      if (
        JSON.stringify(currentTeachers) !== JSON.stringify(initData?.teachers)
      ) {
        setCurrentTeachers(initData?.teachers);
      }

      // reset data in the general form
      setFormData(initData);
    }
  }, [itemData, updatedItem]);

  // General Form action
  const onSubmitForm = data => {
    const updatedData = {
      ...currentElement,
      ...data,
    };
    //console.log({updatedData, data})
    update(updatedData);
    setStayActiveTab(false);
    setFormHaveChanges(false);
    message.success(t("notifications.dataSaved"));
  };

  const setCourseTimeRangeAfterChanges = allFields => {
    if (allFields && Object.keys(allFields).length > 0) {
      if (allFields?.course_time_start && allFields?.course_time_end) {
        const startTime = allFields?.course_time_start,
          endTime = allFields?.course_time_end,
          newTimeValues = checkTimeSetDefaultRange(startTime, endTime);
        if (newTimeValues && newTimeValues?.time_end) {
          setFormFieldsValues({
            course_time_start: newTimeValues?.time_start,
            course_time_end: newTimeValues?.time_end,
          });
        }
      }
    }
  };

  const onFormChange = useCallback((changedFields, allFields) => {
    //console.log(changedFields, allFields)
    checkFromChanges(true);
  }, []);

  const onFormValuesChange = useCallback(
    (changedValues, allValues) => {
      const allValuesObj = Object.assign(allValues);
      const formVals = Object.values(allValues);
      const formHasAnyVals = formVals.some(
        val => val != "" && val != undefined && val != null,
      );
      if (Object.keys(changedValues)[0] == "subject.name") {
        const subject = localSchool?.subjects?.find(
          elem =>
            (elem?.label || elem?.name || elem?.value || elem?.key) ==
            changedValues["subject.name"],
        );
        Object.assign(allValuesObj, {
          "subject.name": changedValues["subject.name"],
          "subject.color": subject?.color || "#cccccc",
        });
      }
      const isEqualValuesWithElement = isEqualFormValues(
        generalForm,
        currentElement,
        allValuesObj,
      );
      if (
        (!isEqualValuesWithElement && currentElement) ||
        (!isEqualValuesWithElement && formHasAnyVals && !currentElement)
      ) {
        checkFromChanges(true);
        setCourseTimeRangeAfterChanges(allValuesObj);
      } else {
        checkFromChanges(false);
      }
    },
    [currentElement, generalForm],
  );

  // Set if form have any changes
  const checkFromChanges = haveChange => {
    setFormHaveChanges(haveChange);
    setStayActiveTab(haveChange);
  };

  /*
   * Сhange the way to switch tabs,
   * if there are changes in the form in the active tab
   */
  const onChangeTab = (key, event) => {
    if (formHaveChanges) {
      event.stopPropagation();
      !stayActiveTab && setActiveTab(key);
    } else {
      setActiveTab(key);
    }
  };

  /*
   * Custom tab title. Added Popconfirm window,
   * if there are changes in the form in the active tab
   */
  const renderTabTitle = useCallback(
    (tabTitle, key) => (
      <RenderPopconfirm
        showPopup={formHaveChanges}
        title={tabTitle}
        key={key}
        onCancel={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          // update data in the general form
          setFormData();
        }}
        onConfirm={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          submitForm();
        }}
      />
    ),
    [formHaveChanges, currentElement],
  );

  // Remove/Add (students, teachers, parents)
  const updateTableList = (array, type) => {
    if (array) {
      const updatedElem = { ...currentElement };
      if (type === "students") {
        updatedElem.students = array;
      } else if (type === "parents") {
        updatedElem.parents = array;
      } else if (type === "courses") {
        updatedElem.courses = array;
      }
      update(updatedElem);
    }
  };

  const deleteCourseCallback = useCallback(() => hardDeleteItem(initID), [
    initID,
  ]);

  let responsible = currentElement?.responsible?.description
    ? `• ${currentElement?.responsible?.description?.lastname} ${
        currentElement?.responsible?.description?.firstname
      }`
    : "";
  let courseDateInfo = `${getDayOfWeekObj(
    currentElement?.course_week,
  )}, ${convertTimeToString(currentElement?.course_time_start, "H:mm")}`;

  const courseClasses =
    currentElement?.classes?.length > 0 &&
    currentElement?.classes?.map(classInfo => classInfo?.title)?.join(", ");
  let courseTitle = `${currentElement?.subject?.name}${
    currentElement?.course_code ? ` • ${currentElement?.course_code}` : ""
  }${
    !!courseClasses ? ` • ${courseClasses}` : ""
  } • ${courseDateInfo} ${responsible}`;

  // render function
  return (
    <div className="details-container">
      <Helmet>
        <title>{t("labels.course")}</title>
        <meta name="description" content="" />
      </Helmet>
      {loading && <Preloader />}
      {!loading && initID && (
        <>
          <CustomPageHeader
            title={
              currentElement?.subject?.name && initID !== newKey
                ? `${courseTitle}`
                : t("courses.newCourse")
            }
            subTitle={t("labels.course")}
            subTitlePosition="top"
            //extraActions={[]}
          />
          <Row gutter={[0, 0]}>
            <Col xs={24}>
              <Tabs
                defaultActiveKey={activeTab}
                activeKey={activeTab}
                className="details-tabs"
                onTabClick={onChangeTab}
              >
                <TabPane
                  tab={renderTabTitle(t("pages.generalInfo"), "general")}
                  key="general"
                  forceRender
                >
                  <Row>
                    <Col span={24}>
                      <CustomForm
                        mode={currentMode}
                        method={formMethod}
                        name={`course-general-form-${initID}`}
                        form={customForm}
                        dataSource={generalForm}
                        handleSubmit={data => onSubmitForm(data)}
                        //handleChange={onFormChange}
                        handleValuesChange={onFormValuesChange}
                        labelCol={24}
                        wrapperCol={24}
                        disabeled={!formHaveChanges}
                      />
                    </Col>
                  </Row>
                  {initID !== newKey && (
                    <Row gutter={(15, 15)} style={{ marginTop: "15px" }}>
                      <Col sm={24} md={24} lg={12} xl={12}>
                        <InfoCard
                          title={t("students.labelNameOf")}
                          editACtion={
                            !formHaveChanges &&
                            initID !== newKey &&
                            (() => setActiveTab("etudiants"))
                          }
                        >
                          {`${(currentStudents && currentStudents.length) ||
                            0} ${t("courses.infoCardLabelStudents")}`}
                        </InfoCard>
                      </Col>
                      <Col sm={24} md={24} lg={12} xl={12}>
                        <InfoCard
                          title={t("courses.courseLeaderLabel")}
                          editACtion={
                            !formHaveChanges &&
                            initID !== newKey &&
                            (() => setActiveTab("enseignant"))
                          }
                        >
                          {currentElement?.responsible?.description
                            ? `${
                                currentElement?.responsible?.description
                                  ?.lastname
                              } ${
                                currentElement?.responsible?.description
                                  ?.firstname
                              }`
                            : "--"}
                        </InfoCard>
                      </Col>
                      <Col xs={24}>
                        {initID &&
                          (process.env.BASE_ENV == "development" ||
                            process.env.BASE_ENV == "local") && (
                            <DeleteButton
                              loading={hardDeleteLoading}
                              deleteHandle={deleteCourseCallback}
                            />
                          )}
                      </Col>
                    </Row>
                  )}
                </TabPane>
                {/* Rendering another tabs */}
                {initID && initID !== newKey && (
                  <>
                    {
                      <TabPane
                        tab={renderTabTitle(
                          t("labels.students"),
                          "etudiants",
                        )}
                        key="etudiants"
                      >
                        <TabStudents
                          showClass={true}
                          students={currentStudents}
                          updateList={array =>
                            updateTableList(array, "students")
                          }
                        />
                      </TabPane>
                    }
                    {
                      <TabPane
                        tab={renderTabTitle(
                          t("labels.teachers"),
                          "enseignant",
                        )}
                        key="enseignant"
                      >
                        <TabTeachers
                          teachers={currentTeachers}
                          sourceData={currentElement}
                          updateSourse={object => onSubmitForm(object)}
                        />
                      </TabPane>
                    }
                    {
                      <TabPane
                        disabled
                        tab={renderTabTitle(
                          t("pages.advancedSettings"),
                          "gestion_avancee",
                        )}
                        key="gestion_avancee"
                      >
                        {initID &&
                          initID !== newKey &&
                          (process.env.BASE_ENV == "development" ||
                            process.env.BASE_ENV == "local") && (
                            <DeleteButton
                              loading={hardDeleteLoading}
                              deleteHandle={deleteCourseCallback}
                            />
                          )}
                      </TabPane>
                    }
                  </>
                )}
              </Tabs>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

CoursesDetail.defaultProps = {
  loading: false,
  mode: "edit",
};
CoursesDetail.propTypes = {
  mode: PropTypes.oneOf(["edit", "onlyRead", "preview", "new"]),
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  update: PropTypes.func,
  loading: PropTypes.bool,
  currentId: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemData: makeSelectItemData(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadCourse: currnetId => dispatch(loadCourse(currnetId)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(CoursesDetail);
